import { useState } from "react";
import axios from "axios";
import { saveWithRetry, errorMessage } from "@/lib/saveWithRetry";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, Wallet } from "lucide-react";
import { ReceiptModal } from "./ReceiptModal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Expense categories for petty cash
const EXPENSE_CATEGORIES = [
  { id: "stock_purchase", name: "Stock/Inventory Purchase" },
  { id: "supplier_payment", name: "Supplier Payment" },
  { id: "office_supplies", name: "Office Supplies" },
  { id: "repairs_maintenance", name: "Repairs & Maintenance" },
  { id: "casual_employee", name: "Casual Employee Payment" },
  { id: "other", name: "Other" }
];

export const PettyCashModal = ({ open, onClose }) => {
  const [amount, setAmount] = useState("");
  const [expenseCategory, setExpenseCategory] = useState("");
  const [description, setDescription] = useState("");
  const [processing, setProcessing] = useState(false);
  const [completedTransaction, setCompletedTransaction] = useState(null);
  const [showReceipt, setShowReceipt] = useState(false);

  // Calculate VAT (15% included in the amount)
  const totalAmount = parseFloat(amount) || 0;
  const subtotal = totalAmount / 1.15;
  const vat = totalAmount - subtotal;

  const handleAmountChange = (e) => {
    const value = e.target.value;
    // Only allow numbers and decimal point
    if (/^\d*\.?\d{0,2}$/.test(value) || value === "") {
      setAmount(value);
    }
  };

  const handleQuickAmount = (value) => {
    setAmount(value.toString());
  };

  const handleSubmit = async () => {
    if (!amount || parseFloat(amount) <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }
    if (!expenseCategory) {
      toast.error("Please select an expense category");
      return;
    }
    if (!description.trim()) {
      toast.error("Please enter a description");
      return;
    }

    setProcessing(true);

    try {
      const pettyCashData = {
        customer_id: null,
        customer_name: null,
        transaction_type: "PETTY CASH",
        items: [{
          product_id: "petty-cash",
          name: EXPENSE_CATEGORIES.find(c => c.id === expenseCategory)?.name || "Petty Cash",
          quantity: 1,
          unit_price: subtotal,
          total: totalAmount
        }],
        subtotal: subtotal,
        discount: 0,
        tax: vat,
        total: totalAmount,
        payments: [{ method: "PETTY CASH OUT", amount: totalAmount }],
        cashier: "Cashier 1",
        price_level: 1,
        expense_category: expenseCategory,
        petty_cash_description: description
      };

      const response = await saveWithRetry(() => axios.post(`${API}/sales/transaction`, pettyCashData));
      
      const categoryName = EXPENSE_CATEGORIES.find(c => c.id === expenseCategory)?.name || expenseCategory;
      toast.success(`Petty Cash Out of R ${totalAmount.toFixed(2)} recorded (${categoryName})`);
      
      setCompletedTransaction(response.data);
      setShowReceipt(true);
    } catch (error) {
      console.error("Error processing petty cash:", error);
      toast.error(errorMessage(error, "Failed to process petty cash"));
    } finally {
      setProcessing(false);
    }
  };

  const handleReceiptClose = () => {
    setShowReceipt(false);
    setCompletedTransaction(null);
    // Reset form
    setAmount("");
    setExpenseCategory("");
    setDescription("");
    onClose();
  };

  const handleClose = () => {
    if (!processing) {
      setAmount("");
      setExpenseCategory("");
      setDescription("");
      onClose();
    }
  };

  // Show receipt modal after transaction
  if (showReceipt && completedTransaction) {
    return (
      <ReceiptModal
        open={showReceipt}
        onClose={handleReceiptClose}
        transaction={completedTransaction}
      />
    );
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-green-700">
            <Wallet className="h-6 w-6" />
            Petty Cash Out
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Amount Input */}
          <div className="border-2 border-green-200 rounded-lg p-4 bg-green-50">
            <Label className="text-lg font-semibold text-green-800 mb-3 block">
              Amount to Pay Out
            </Label>
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-3xl font-bold text-green-700">R</span>
              <Input
                type="text"
                value={amount}
                onChange={handleAmountChange}
                placeholder="0.00"
                className="text-4xl font-bold text-center h-20 pl-12 pr-4 border-green-300 focus:border-green-500"
                autoFocus
              />
            </div>
            
            {/* Quick Amount Buttons */}
            <div className="grid grid-cols-4 gap-2 mt-4">
              {[50, 100, 200, 500].map((val) => (
                <Button
                  key={val}
                  type="button"
                  variant="outline"
                  onClick={() => handleQuickAmount(val)}
                  className="border-green-300 text-green-700 hover:bg-green-100"
                >
                  R{val}
                </Button>
              ))}
            </div>

            {/* VAT Breakdown */}
            {totalAmount > 0 && (
              <div className="mt-4 pt-4 border-t border-green-200 text-sm text-green-700">
                <div className="flex justify-between">
                  <span>Amount Excl VAT:</span>
                  <span>R {subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span>VAT (15%):</span>
                  <span>R {vat.toFixed(2)}</span>
                </div>
              </div>
            )}
          </div>

          {/* Expense Category */}
          <div>
            <Label className="mb-2 block font-semibold">Expense Category *</Label>
            <Select value={expenseCategory} onValueChange={setExpenseCategory}>
              <SelectTrigger className="w-full h-12">
                <SelectValue placeholder="Select expense category..." />
              </SelectTrigger>
              <SelectContent>
                {[...EXPENSE_CATEGORIES].sort((a, b) => (a.name || '').localeCompare(b.name || '')).map((cat) => (
                  <SelectItem key={cat.id} value={cat.id}>
                    {cat.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Description */}
          <div>
            <Label className="mb-2 block font-semibold">Description *</Label>
            <Textarea
              placeholder="Enter description (e.g., Supplier name, what was purchased, invoice number)..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="min-h-[100px]"
            />
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4">
            <Button
              variant="outline"
              onClick={handleClose}
              className="flex-1 h-14 text-lg"
              disabled={processing}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              className={`flex-1 h-14 text-lg ${
                totalAmount > 0 && expenseCategory && description.trim()
                  ? "bg-green-600 hover:bg-green-700"
                  : "bg-gray-400"
              }`}
              disabled={processing || !totalAmount || !expenseCategory || !description.trim()}
            >
              {processing ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  Processing...
                </>
              ) : !totalAmount ? (
                "Enter Amount"
              ) : !expenseCategory ? (
                "Select Category"
              ) : !description.trim() ? (
                "Enter Description"
              ) : (
                <>
                  <Wallet className="h-5 w-5 mr-2" />
                  PAY OUT R {totalAmount.toFixed(2)}
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PettyCashModal;
